/**
 * Shared-world room plumbing: the fixed room every Play lands in, its save
 * namespace, `?room=CODE` private rooms, the one-time single-player -> room
 * save copy, the player name derived from the identity key, and transport
 * selection with an offline (loopback) fallback.
 *
 * Deliberately free of DOM / WebRTC imports so the node smoke suite can
 * cover it (src/smoke/netflow.ts); net/index.ts wires it to the real page.
 */

import { CHUNK_SIZE, WORLD_HEIGHT } from '../constants';
import { CHUNK_AREA } from '../world/chunk';
import { LoopbackHub } from './transport';
import type { Transport } from './transport';

/** Every Play joins this room; the worldgen version keeps old worlds apart. */
export const MAIN_ROOM_PREFIX = 'MAINV';
/** How long the title flow waits for a host before hosting the room alone. */
export const ROOM_DISCOVER_MS = 1200;
/** Offline (no signaling): nobody can answer, so host at once. */
export const OFFLINE_DISCOVER_MS = 120;
/** Budget per transport before falling through to the next candidate. */
export const TRANSPORT_TIMEOUT_MS = 4000;

/** "MAINV3" for worldgen v3: the room the shared world lives in. */
export function mainRoomCode(wgv: number): string { return MAIN_ROOM_PREFIX + wgv; }

/** Every room keeps its world (and this peer's cache of it) under its own key. */
export function roomSaveKey(code: string, wgv: number): string { return `opencraft.mp.v${wgv}.${code}`; }

/**
 * Room codes travel in URLs, the signaling path and localStorage keys, so
 * keep them to something inert and uppercase. The main room contains an "I",
 * which the old hand-typed 6-char alphabet excluded, so this only rejects
 * punctuation and over-long codes.
 */
export function normalizeRoomCode(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const c = raw.trim().toUpperCase();
  return /^[A-Z0-9]{3,12}$/.test(c) ? c : null;
}

/** `?room=CODE` selects a private room; anything else leaves the main room. */
export function roomFromSearch(search: string): string | null {
  try { return normalizeRoomCode(new URLSearchParams(search).get('room')); } catch { return null; }
}

/** "Player1A7C": stable for one identity, so a returning player keeps its name. */
export function playerNameFromKey(id: string): string {
  const tail = (id || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(-4);
  return 'Player' + (tail || '0000');
}

export interface StorageLike { getItem(k: string): string | null; setItem(k: string, v: string): void; removeItem?(k: string): void }

/**
 * First run in a room: adopt the old single-player save as the room's world.
 * Only ever copies into a room that has nothing cached, so a room's own
 * history is never overwritten by a stale local world.
 */
export function ensureRoomSave(storage: StorageLike, spKey: string, room: string): boolean {
  if (!spKey || spKey === room) return false;
  try {
    if (storage.getItem(room)) return false;
    const sp = storage.getItem(spKey);
    if (!sp) return false;
    storage.setItem(room, sp);
    return true;
  } catch {
    return false;
  }
}

/**
 * Player state lives per identity, not per room. Two tabs of one browser are
 * two players (they even sign with different keys), so sharing one inventory
 * would clone the first player into the second tab. Each identity therefore
 * keeps its own copy next to the room's world, and anyone without one starts
 * empty - a fresh joiner and a second tab take exactly the same path.
 */
export function playerSaveKey(code: string, wgv: number, identityId: string): string {
  const id = (identityId || '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 16);
  return `opencraft.mp.p${wgv}.${code}.${id || 'anon'}`;
}

/** The player half of a save file: everything that is not world state. */
export const PLAYER_FIELDS = ['player', 'hotbar', 'inventory', 'stats'] as const;
export type PlayerField = (typeof PLAYER_FIELDS)[number];
export interface PlayerStateFile { player?: unknown; hotbar?: unknown; inventory?: unknown; stats?: unknown }

/** true when the player half holds anything (a clean player holds nothing). */
export function hasPlayerState(p: PlayerStateFile | null | undefined): boolean {
  return !!p && PLAYER_FIELDS.some((f) => p[f] !== undefined && p[f] !== null);
}

/** Pull the player half out of a save file (null when the player is clean). */
export function extractPlayerState(file: unknown): PlayerStateFile | null {
  if (!file || typeof file !== 'object') return null;
  const src = file as Record<string, unknown>;
  const out: PlayerStateFile = {};
  let any = false;
  for (const f of PLAYER_FIELDS) {
    const v = src[f];
    if (v === undefined || v === null) continue;
    out[f] = v;
    any = true;
  }
  return any ? out : null;
}

/** Write a save file with its player half replaced (null = clean player). */
export function withPlayerState(file: unknown, player: PlayerStateFile | null): string {
  const out: Record<string, unknown> = file && typeof file === 'object' ? { ...(file as Record<string, unknown>) } : {};
  for (const f of PLAYER_FIELDS) {
    const v = player?.[f];
    if (v === undefined || v === null) delete out[f];
    else out[f] = v;
  }
  return JSON.stringify(out);
}

/**
 * Upgrade path: a room save written before player state moved out still has
 * one player's state in it. Hand it to the first identity that starts in that
 * room and mark the room, so every later tab (or player) starts clean instead
 * of cloning it. The marker is per room, which is the whole point: one
 * browser, one world, many players.
 */
export function adoptLegacyPlayerState(storage: StorageLike, roomKey: string, playerKey: string): boolean {
  const marker = roomKey + '.player-adopted';
  try {
    if (storage.getItem(playerKey) || storage.getItem(marker)) return false;
    const room = storage.getItem(roomKey);
    if (!room) return false;
    const mine = extractPlayerState(JSON.parse(room));
    if (!hasPlayerState(mine)) return false;
    storage.setItem(playerKey, JSON.stringify(mine));
    storage.setItem(marker, '1');
    return true;
  } catch {
    return false;
  }
}

/**
 * Point a loaded save at one identity's player state: adopt the room's old
 * player once (upgrade path), load our own half into the save, and mirror the
 * player fields back into our key on every flush. World state in the room file
 * is left alone.
 *
 * Returns what happened, for the boot log. Any player without a key of their
 * own starts clean - a second tab takes exactly the path of a fresh joiner.
 */
export function bindPlayerState(
  save: SaveInternals & { flush(force?: boolean): void },
  storage: StorageLike,
  roomKey: string,
  playerKey: string,
  now: () => number = () => performance.now(),
): { adopted: boolean; clean: boolean } {
  const adopted = adoptLegacyPlayerState(storage, roomKey, playerKey);
  let mine: PlayerStateFile | null = null;
  try {
    const raw = storage.getItem(playerKey);
    if (raw) mine = extractPlayerState(JSON.parse(raw));
  } catch { /* unreadable sidecar: play clean */ }
  save.player = mine?.player ?? null;
  save.hotbar = mine?.hotbar ?? null;
  save.inventory = mine?.inventory ?? null;
  save.stats = mine?.stats ?? null;

  const flush = save.flush.bind(save);
  let last = 0;
  save.flush = (force = false): void => {
    flush(force);
    const t = now();
    if (!force && t - last < 1000) return; // the save throttles writes too
    last = t;
    const p = extractPlayerState(save);
    try {
      if (hasPlayerState(p)) storage.setItem(playerKey, JSON.stringify(p));
      else storage.removeItem?.(playerKey);
    } catch { /* storage full: retried on the next flush */ }
  };
  return { adopted, clean: !hasPlayerState(mine) };
}

/** WorldSave's block edits, read structurally (net-only coupling, see save.ts). */
interface DimInternals {
  edits?: Map<number, Map<number, number>>;
  coords?: Map<number, string>;
  chests?: Map<string, unknown>;
  furnaces?: Record<string, number[]>;
}

export interface SaveInternals {
  storageKey?: string;
  player?: unknown;
  time?: unknown;
  hotbar?: unknown;
  inventory?: unknown;
  stats?: unknown;
  spawn?: unknown;
  dimension?: string;
  entities?: Record<string, unknown>;
  existed?: boolean;
  edits?: Map<number, Map<number, number>>;
  coords?: Map<number, string>;
  chests?: Map<string, unknown>;
  dims?: Record<string, DimInternals>;
  load?(): void;
}

function dims(s: SaveInternals): DimInternals[] {
  const all = s.dims && typeof s.dims === 'object' ? Object.values(s.dims) : [];
  if (all.length) return all.filter((d): d is DimInternals => !!d && typeof d === 'object');
  // pre-dimension layout: the save exposes one current-dimension map
  return [{ edits: s.edits, coords: s.coords, chests: s.chests }];
}

/**
 * Forget every cached block edit (all dimensions) and report how many blocks
 * that was. Used before importing a host snapshot: anything the host did not
 * send is a divergent local edit and must not survive the sync.
 */
export function clearSaveEdits(s: SaveInternals): number {
  let n = 0;
  for (const d of dims(s)) {
    for (const m of d.edits?.values() ?? []) n += m.size;
    d.edits?.clear();
    d.coords?.clear();
  }
  return n;
}

/** Wipe a save's in-memory state (before re-loading it from another namespace). */
export function clearSaveMemory(s: SaveInternals): void {
  for (const d of dims(s)) {
    d.edits?.clear();
    d.coords?.clear();
    d.chests?.clear();
    if (d.furnaces) d.furnaces = {};
  }
  s.player = null;
  s.time = null;
  s.hotbar = null;
  s.inventory = null;
  s.stats = null;
  s.spawn = null;
  s.entities = {};
  s.dimension = 'overworld';
  s.existed = false;
}

/** World-server dimension numbers (signaling/src/worldCore.ts) and the save's names. */
export const DIM_NAMES = ['overworld', 'nether'] as const;

/** The slice of a WorldSave the world-server boot reads and writes. */
export interface SaveWorldTarget extends SaveInternals {
  record?(cx: number, cz: number, index: number, id: number): void;
  setSpawn?(p: { x: number; y: number; z: number } | null): void;
  setFlag?(k: string): void;
}

/** What `bootWorld` (net/worldServer.ts) needs from the page's save. */
export interface SaveBootTarget {
  exportBlocks(): Array<[number, number, number, number, number]>;
  clearBlocks(): void;
  putBlock(dim: number, x: number, y: number, z: number, id: number): void;
  time(): number;
  setTime(t: number): void;
  addFlag(f: string): void;
  setPlayer(p: Record<string, unknown>): void;
}

/**
 * Point the world-server boot at a loaded save, before any chunk exists.
 * Blocks go through the same `record(cx, cz, voxelIndex, id)` the game uses, in
 * the dimension the server names, so chunk generation bakes them in.
 */
export function saveBootTarget(save: SaveWorldTarget): SaveBootTarget {
  const inDim = (dim: number, fn: () => void): void => {
    const name = DIM_NAMES[dim];
    if (!name) return;
    const here = save.dimension;
    save.dimension = name;
    try { fn(); } finally { save.dimension = here; }
  };
  return {
    exportBlocks: () => {
      const out: Array<[number, number, number, number, number]> = [];
      DIM_NAMES.forEach((name, dim) => {
        // pre-dimension saves expose one map: that one is the overworld
        const d: DimInternals | undefined = save.dims?.[name] ?? (dim === 0 ? save : undefined);
        if (!(d?.edits instanceof Map) || !(d.coords instanceof Map)) return;
        for (const [key, m] of d.edits) {
          const coord = d.coords.get(key);
          if (!coord) continue;
          const [cx, cz] = coord.split(',').map(Number);
          if (!Number.isInteger(cx) || !Number.isInteger(cz)) continue;
          for (const [idx, id] of m) {
            const y = Math.floor(idx / CHUNK_AREA);
            if (y < 0 || y >= WORLD_HEIGHT || !Number.isInteger(id) || id < 0 || id > 0xffff) continue;
            out.push([dim, cx * CHUNK_SIZE + (idx & 15), y, cz * CHUNK_SIZE + ((idx >> 4) & 15), id]);
          }
        }
      });
      return out;
    },
    clearBlocks: () => { clearSaveEdits(save); },
    putBlock: (dim, x, y, z, id) => {
      if (!save.record || y < 0 || y >= WORLD_HEIGHT) return;
      inDim(dim, () => save.record?.(x >> 4, z >> 4, (x & 15) + (z & 15) * CHUNK_SIZE + y * CHUNK_AREA, id));
    },
    time: () => (typeof save.time === 'number' && Number.isFinite(save.time) ? save.time : 0.25),
    setTime: (t) => { save.time = t; },
    addFlag: (f) => { if (f.length > 0 && f.length <= 64) save.setFlag?.(f); },
    setPlayer: (p) => {
      const mine = extractPlayerState(p);
      for (const f of PLAYER_FIELDS) save[f] = mine?.[f] ?? null;
      const sp = p.spawn as { x?: unknown; y?: unknown; z?: unknown } | null | undefined;
      if (sp && [sp.x, sp.y, sp.z].every((n) => typeof n === 'number' && Number.isFinite(n))) {
        save.setSpawn?.({ x: sp.x as number, y: sp.y as number, z: sp.z as number });
      }
    },
  };
}

/** What a page uploads as its player: the player half of the save plus the bed spawn. */
export function playerUpload(save: SaveInternals): Record<string, unknown> | null {
  const p = extractPlayerState(save);
  if (!p) return null;
  return { ...p, spawn: save.spawn ?? null };
}

export interface TransportCandidate { label: string; make: () => Promise<Transport>; ms?: number }
export interface ConnectedTransport { t: Transport; label: string; solo: boolean; notes: string[] }

/** A hub nobody else can join: the offline / signaling-broken fallback. */
export function soloTransport(id = 'solo'): Transport { return new LoopbackHub().join(id); }

/**
 * Transport selection with hard fallbacks. Each candidate gets a time budget;
 * a throw or a stall drops to the next one, and the last resort is an
 * in-memory solo link so a player with no network still ends up in the world.
 */
export async function connectTransport(cands: TransportCandidate[], opts: { onNote?: (s: string) => void } = {}): Promise<ConnectedTransport> {
  const notes: string[] = [];
  const note = (s: string): void => { notes.push(s); opts.onNote?.(s); };
  for (const c of cands) {
    let p: Promise<Transport>;
    try { p = c.make(); } catch (e) { note(`${c.label} unavailable (${errText(e)})`); continue; }
    try {
      const t = await withBudget(p, c.ms ?? TRANSPORT_TIMEOUT_MS, c.label);
      return { t, label: c.label, solo: false, notes };
    } catch (e) {
      note(`${c.label} unavailable (${errText(e)})`);
    }
  }
  note('Offline: hosting the world alone, nobody can join');
  return { t: soloTransport(), label: 'Offline (solo)', solo: true, notes };
}

/** Resolve `p` within `ms`, closing whatever a late transport still built. */
function withBudget(p: Promise<Transport>, ms: number, label: string): Promise<Transport> {
  return new Promise<Transport>((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      void p.then((t) => t.close()).catch(() => {});
      reject(new Error(`no answer from ${label} within ${ms}ms`));
    }, ms);
    p.then((t) => {
      if (settled) { t.close(); return; }
      settled = true;
      clearTimeout(timer);
      resolve(t);
    }, (e) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      reject(e instanceof Error ? e : new Error(String(e)));
    });
  });
}

function errText(e: unknown): string {
  if (e instanceof Error) return e.message;
  const s = String(e);
  return s.length > 80 ? s.slice(0, 77) + '...' : s;
}
